import { useMutation } from '@tanstack/react-query';
import { chakra, Input, Flex } from '@chakra-ui/react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams, useRouteLoaderData } from 'react-router-dom';
import { api, CompanyDto, UpdateCompanyDto } from '../../lib/api';
import { CreateCompanyDto } from '../../lib/api';
import { accreditationBodies } from '../../utils/coding-details';
import Address from '../../components/address';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import { Button } from '@/components/ui/button';
import { toaster } from '@/components/ui/toaster';
import { Card } from '@/components/ui/card';
import { zodResolver } from '@hookform/resolvers/zod';
import { companyValidationSchema } from '@musa/lib';
import { SoftwareFeatures } from '../forms/shared/software-features';
import { z } from 'zod';

const defaultValues: CreateCompanyDto = {
    name: '',
    address: {
        address: '',
        city: '',
        state: '',
        zipcode: '',
    },
    agencyId: '',
    taxId: '',
    npi: '',
    ptan: '',
    accreditationBody: '',
    accreditationId: '',
    services: {
        homeHealth: false,
        homeHealthServices: {
            SN: false,
            PT: false,
            OT: false,
            ST: false,
            MSW: false,
            HHA: false,
        },
        pca: false,
        hospice: false,
    },
};

const companyValidationSchemaExtended = companyValidationSchema.extend({
    agencyId: z.string().min(1, 'required'),
    accreditationBody: z.string().min(1, 'required'),
    accreditationId: z.string().min(1, 'required'),
});

export function CompanyPage() {
    const navigate = useNavigate();
    const data = useRouteLoaderData('company-details') as CompanyDto;
    const { companyId } = useParams<'companyId'>();

    // save data
    const { mutate, isPending } = useMutation<unknown, { error: Error }, UpdateCompanyDto | CreateCompanyDto>({
        mutationFn: async (data) => {
            if (companyId) {
                return api.company.update(companyId, data as UpdateCompanyDto);
            }
            return api.company.create(data as CreateCompanyDto);
        },
        onSuccess: () => {
            navigate('/organization/overview');
        },
        onError: (error) => {
            toaster.create({
                title: `Unable to create company: ${error.error.message}`,
                type: 'error',
                duration: 3000,
            });
        },
    });

    // handlers
    const onSubmit: SubmitHandler<CreateCompanyDto> = async (data) => {
        mutate(data);
    };

    const rhf = useForm<CreateCompanyDto>({
        defaultValues: { ...defaultValues, ...data, services: { ...defaultValues.services, ...data?.services } },
        resolver: zodResolver(companyValidationSchemaExtended),
        reValidateMode: 'onBlur',
    });

    const {
        formState: { errors },
        register,
    } = rhf;

    return (
        <Flex gap="8" flexDirection="column" maxW="600px">
            <FormProvider {...rhf}>
                <chakra.form noValidate onSubmit={rhf.handleSubmit(onSubmit)} w="100%">
                    <Flex flexDirection="column" gap="24px">
                        <Card type="standard" barTitle="Company Details" w="100%">
                            <Field label="Company name" required invalid={!!errors?.name} errorText={errors?.name?.message}>
                                <Input type="text" {...register('name')} />
                            </Field>
                            <Address
                                isRequired={true}
                                validAddress={!!errors?.address?.address}
                                validCity={!!errors?.address?.city}
                                validState={!!errors?.address?.state}
                                validZipCode={!!errors?.address?.zipcode}
                                errorAddress={errors?.address?.address?.message}
                                errorCity={errors?.address?.city?.message}
                                errorState={errors?.address?.state?.message}
                                errorZipCode={errors?.address?.zipcode?.message}
                                nameAddress="address.address"
                                nameCity="address.city"
                                nameState="address.state"
                                nameZipCode="address.zipcode"
                            />
                        </Card>
                        <Card type="standard" barTitle="Licenses" w="100%">
                            <FieldGroup>
                                <FieldRow>
                                    <Field
                                        label="State Agency ID"
                                        required
                                        invalid={!!errors.agencyId}
                                        errorText={errors.agencyId?.message}>
                                        <Input type="text" {...register('agencyId')} />
                                    </Field>
                                    <Field label="Federal Tax ID" required invalid={!!errors?.taxId} errorText={errors?.taxId?.message}>
                                        <Input type="text" {...register('taxId')} maxLength={9} data-testid="input-companyTaxId" />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field label="NPI" required invalid={!!errors?.npi} errorText={errors?.npi?.message}>
                                        <Input type="text" {...register('npi')} maxLength={10} data-testid="input-companyNpi" />
                                    </Field>
                                    <Field label="PTAN" required invalid={!!errors?.ptan} errorText={errors?.ptan?.message}>
                                        <Input type="text" {...register('ptan')} maxLength={6} data-testid="input-companyPtan" />
                                    </Field>
                                </FieldRow>
                                <FieldRow>
                                    <Field
                                        label="Accreditation Body"
                                        required
                                        invalid={!!errors.accreditationBody}
                                        errorText={errors.accreditationBody?.message}>
                                        <NativeSelectRoot>
                                            <NativeSelectField {...register('accreditationBody')}>
                                                {accreditationBodies.map((body) => {
                                                    return <option key={body}>{body}</option>;
                                                })}
                                            </NativeSelectField>
                                        </NativeSelectRoot>
                                    </Field>
                                    <Field
                                        label="Accreditation ID"
                                        required
                                        invalid={!!errors.accreditationId}
                                        errorText={errors.accreditationId?.message}>
                                        <Input type="text" {...register('accreditationId')} id="accreditationId" name="accreditationId" />
                                    </Field>
                                </FieldRow>
                            </FieldGroup>
                        </Card>
                        <Card type="standard" barType={errors.services ? 'error' : 'standard'} barTitle="Software Features" w="100%">
                            <SoftwareFeatures<CreateCompanyDto>
                                hh={{
                                    path: 'services.homeHealth',
                                }}
                                hhServices={{
                                    path: 'services.homeHealthServices',
                                }}
                                hospice={{
                                    path: 'services.hospice',
                                }}
                                pca={{
                                    path: 'services.pca',
                                }}
                                error={!!errors.services}
                                errorMessage={errors.services?.message || errors.services?.root?.message}
                            />
                        </Card>
                        <Flex>
                            <Button type="submit" disabled={rhf.formState.isSubmitting} loadingText="saving" loading={isPending}>
                                {data ? 'Save' : 'Create'}
                            </Button>
                        </Flex>
                    </Flex>
                </chakra.form>
            </FormProvider>
        </Flex>
    );
}
